import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { useProducts } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { IMG } from "@/lib/content";
import { TrustBar, Marquee, Results, Reviews } from "@/components/shop/Sections";
import { Bundle } from "@/components/shop/Bundle";
import { SplitText, Reveal } from "@/components/fx/Motion";
import { Hero, Lifestyle } from "@/components/shop/Hero";
import { ProductCarousel } from "@/components/shop/ProductCarousel";
import { HolidayBanner, GiftSets } from "@/components/shop/HolidaySections";
import { useSeason } from "@/lib/holiday";
import { useSeo } from "@/lib/seo";
import { InstagramGallery } from "@/components/shop/InstagramGallery";

const EASE = [0.22, 1, 0.36, 1];

const BrandTile = ({ b, i }) => {
  const { t } = useI18n();
  return (
    <Link to={`/collections/${b.h}`} data-testid={`brand-tile-${b.h}`} className="group relative aspect-[4/3] overflow-hidden bg-white block">
      <motion.div className="absolute inset-0" initial={{ clipPath: "inset(100% 0 0 0)" }} whileInView={{ clipPath: "inset(0% 0 0 0)" }} viewport={{ once: true }} transition={{ duration: 1.4, ease: [0.76, 0, 0.24, 1], delay: i * 0.2 }}>
        <img src={b.img} alt={b.name} className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.6s] ease-out group-hover:scale-110" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#0b0a08]/95 via-[#0b0a08]/25 to-transparent transition-opacity duration-700 group-hover:opacity-80" />
      <div className="absolute bottom-0 left-0 p-8 lg:p-12">
        <p className="eyebrow mb-2">{t(b.sub)}</p>
        <p className="font-display text-4xl lg:text-6xl text-white"><SplitText text={b.name} delay={0.4 + i * 0.2} /></p>
        <span className="inline-flex items-center gap-2 mt-4 text-xs uppercase tracking-[0.2em] text-white/80 group-hover:text-gold group-hover:gap-4 transition-[gap,color] duration-500">{t("shop_collection")} <ArrowRight size={14} /></span>
      </div>
    </Link>
  );
};

const BRAND_TILES = [
  { h: "primus", name: "Primus", sub: "brand_primus_sub", img: IMG.primus },
  { h: "bellecare", name: "Bellecare Natural", sub: "brand_bellecare_sub", img: IMG.bellecare },
];

export default function Home() {
  const { active } = useSeason();
  const { t } = useI18n();
  const { data = [], isLoading } = useProducts({ collection: "frontpage" });
  useSeo(null);
  return (
    <div data-testid="home-page">
      <HolidayBanner />
      <Hero />
      <Marquee />
      <section className="max-w-7xl mx-auto px-5 lg:px-10 py-12 lg:py-16"><TrustBar /></section>
      <section data-testid="bestsellers-section" className="max-w-7xl mx-auto px-5 lg:px-10 pb-28">
        <div className="flex items-end justify-between mb-12">
          <div>
            <p className="eyebrow mb-4">{t("best_eyebrow")}</p>
            <h2 className="font-display text-3xl lg:text-5xl"><SplitText text={t("best_title")} /></h2>
          </div>
          <Reveal><Link data-testid="bestsellers-view-all" to="/shop" className="hidden sm:flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-gold hover:text-white hover:gap-4 transition-[gap,color] duration-500">{t("view_all")} <ArrowRight size={14} /></Link></Reveal>
        </div>
        <ProductCarousel products={data} loading={isLoading} testid="bestsellers-carousel" />
      </section>
      {active && <GiftSets />}
      <section data-testid="brands-section" className="grid md:grid-cols-2">
        {BRAND_TILES.map((b, i) => <BrandTile key={b.h} b={b} i={i} />)}
      </section>
      <Lifestyle />
      <Bundle />
      <Reviews />
      <Results />
      <InstagramGallery />
    </div>
  );
}
